"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./ui/card"
import { Button } from "./ui/button"
import { Switch } from "./ui/switch"
import { Rocket } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { usePricing } from "../context/PricingContext";
import { useTrial } from "../context/TrialContext";
import { formatPerMonth } from "../utils/pricingDisplay";
import {
  isPremiumActive,
  isStarterPlan,
  isElitePlan,
} from "../utils/accountLimitsUtils";
import { subscriptionService } from "../apis/subscriptionService";

function planDisplayName(planKey) {
  const key = String(planKey || "").toLowerCase();
  if (key === "starter") return "Starter";
  if (key === "elite") return "Elite";
  if (key === "professional") return "Professional";
  return key ? key.charAt(0).toUpperCase() + key.slice(1) : "Free";
}

function formatCents(cents) {
  if (cents == null || Number.isNaN(Number(cents))) return null;
  return `$${(Number(cents) / 100).toFixed(2)}`;
}

export function BillingSettings() {
  const navigate = useNavigate();
  const { getAmount, loading } = usePricing();
  const { trialStatus, refreshTrialStatus } = useTrial();

  const [emrLoading, setEmrLoading] = useState(false);
  const [emrSaving, setEmrSaving] = useState(false);
  const [emrSettings, setEmrSettings] = useState(null);

  const isPremium = isPremiumActive(trialStatus);
  const softTrial = Boolean(trialStatus?.isTrial);
  const starter = isStarterPlan(trialStatus);
  const elite = isElitePlan(trialStatus);
  const planKey =
    trialStatus?.planKey ||
    trialStatus?.accountLimits?.planKey ||
    (softTrial ? "professional" : null);

  const upgradePlanKey = elite
    ? "elite"
    : starter || softTrial || !isPremium
      ? "professional"
      : "elite";

  const annualPrice = getAmount(upgradePlanKey, "annually", 1);
  const monthlyPrice = getAmount(upgradePlanKey, "monthly", 1);
  const priceHint =
    formatPerMonth(annualPrice, { suffix: "/mo (billed annually)" }) ||
    formatPerMonth(monthlyPrice) ||
    "See pricing";

  const daysRemaining =
    trialStatus?.daysRemaining ??
    trialStatus?.accountLimits?.trialPeriod?.daysRemaining ??
    null;

  const planTitle = useMemo(() => {
    if (softTrial) return "Professional";
    if (isPremium) return planDisplayName(planKey);
    return "Free";
  }, [softTrial, isPremium, planKey]);

  const planSubtitle = useMemo(() => {
    if (softTrial) {
      const days =
        daysRemaining != null && daysRemaining >= 0
          ? Math.ceil(Number(daysRemaining))
          : trialStatus?.trialDays || 7;
      return `Your Professional trial ends in ${days} day${days === 1 ? "" : "s"}`;
    }
    if (starter) {
      return "50 AI SOAP notes/month · 30-day note history";
    }
    if (elite) {
      return "Everything in Professional · early access · priority requests · EMR $1.29/chart";
    }
    if (isPremium) {
      return "Unlimited notes · Magic Editor & HPI · optional EMR input ($1.99/chart)";
    }
    return "Start a 7-day Professional free trial after onboarding";
  }, [softTrial, daysRemaining, trialStatus?.trialDays, starter, elite, isPremium]);

  const showUpgrade =
    softTrial || starter || !isPremium || String(planKey).toLowerCase() !== "elite";

  const loadEmrSettings = useCallback(async () => {
    setEmrLoading(true);
    try {
      const data = await subscriptionService.getEmrSettings();
      setEmrSettings(data);
    } catch (err) {
      console.error("Failed to load EMR settings:", err);
      setEmrSettings(null);
    } finally {
      setEmrLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmrSettings();
  }, [loadEmrSettings, trialStatus?.planKey, trialStatus?.isTrial]);

  const handleUpgradeClick = () => {
    sessionStorage.setItem(
      "vbot_payment_plan",
      JSON.stringify({
        planKey: upgradePlanKey,
        billingPeriod: "annually",
        seatCount: 1,
      })
    );
    navigate("/payment");
  };

  const handleEmrToggle = async (enabled) => {
    setEmrSaving(true);
    try {
      const data = await subscriptionService.setEmrEnabled(enabled);
      setEmrSettings((prev) => ({
        ...(prev || {}),
        ...data,
        emrInputEligible: prev?.emrInputEligible ?? true,
      }));
      toast.success(enabled ? "EMR input enabled" : "EMR input disabled");
      refreshTrialStatus?.();
    } catch (err) {
      const msg =
        err?.response?.data?.body?.message ||
        err?.response?.data?.message ||
        err?.message ||
        "Could not update EMR settings";
      toast.error(msg);
    } finally {
      setEmrSaving(false);
    }
  };

  const emrEligible =
    emrSettings?.emrInputEligible ??
    trialStatus?.emrInputEligible ??
    trialStatus?.accountLimits?.emrInputEligible ??
    false;
  const defaultEmrCents = elite ? 129 : 199;
  const emrPriceLabel =
    formatCents(
      emrSettings?.emrInputPriceCents ??
        trialStatus?.emrInputPriceCents ??
        trialStatus?.accountLimits?.emrInputPriceCents ??
        defaultEmrCents
    ) || (elite ? "$1.29" : "$1.99");
  const emrEnabled = Boolean(emrSettings?.emrInputEnabled);
  const sameDayEmr =
    emrSettings?.turnaround === "same_day" ||
    trialStatus?.emrTurnaround === "same_day" ||
    elite;

  return (
    <Card className="w-full max-w-3xl mx-auto border-2 border-gray-200 bg-white">
      <CardHeader>
        <CardTitle>Billing</CardTitle>
        <h1>Plan</h1>
      </CardHeader>
      <CardContent className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2>
              {planTitle}{" "}
              {softTrial && (
                <span className="text-green-600 bg-green-100 text-xs px-2 py-1 rounded">
                  Free Trial
                </span>
              )}
              {isPremium && !softTrial && starter && (
                <span className="text-blue-700 bg-blue-100 text-xs px-2 py-1 rounded">
                  Starter
                </span>
              )}
              {elite && (
                <span className="text-violet-700 bg-violet-100 text-xs px-2 py-1 rounded">
                  Elite
                </span>
              )}
            </h2>
            <CardDescription className="text-gray-600">
              {planSubtitle}
            </CardDescription>
            {elite && (
              <ul className="mt-3 text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>Early access to new features</li>
                <li>Priority feature requests</li>
                <li>EMR input at {emrPriceLabel}/chart (same-day)</li>
              </ul>
            )}
            {!loading && showUpgrade && (
              <p className="text-sm text-gray-700 mt-2">
                {elite ? "Elite" : planDisplayName(upgradePlanKey)} from {priceHint}
                {monthlyPrice != null && annualPrice != null && (
                  <span className="text-gray-500">
                    {" "}
                    · or {formatPerMonth(monthlyPrice)} monthly
                  </span>
                )}
              </p>
            )}
          </div>
          {showUpgrade && (
            <Button
              className="border border-gray-400 text-sm font-medium px-4 py-2 bg-blue-600 text-white"
              onClick={handleUpgradeClick}
            >
              {softTrial || !isPremium
                ? "Upgrade Now"
                : elite
                  ? "Manage plan"
                  : "Upgrade to Elite"}{" "}
              <Rocket />
            </Button>
          )}
        </div>

        {emrEligible && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-gray-100 pt-6">
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Optional EMR input
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Human EHR entry at {emrPriceLabel}/chart
                {sameDayEmr
                  ? " · same-day turnaround"
                  : " · typically within 24 hours"}
                . Enable here, then request EMR input from a chart.
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {(emrLoading || emrSaving) && (
                <span className="text-xs text-gray-500">Saving…</span>
              )}
              <Switch
                checked={emrEnabled}
                disabled={emrLoading || emrSaving}
                onCheckedChange={handleEmrToggle}
                aria-label="Enable optional EMR input"
              />
            </div>
          </div>
        )}
      </CardContent>
      <CardFooter />
    </Card>
  );
}
